import { ClientContentItemEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';

/**
 * A page's URL as two addresses of one page compare equal: the origin and path lower-cased, a
 * trailing slash dropped, the query kept. A value that does not parse as a URL is compared as typed,
 * trimmed and lower-cased.
 */
export function normalizePageUrl(urlValue: string): string {
  try {
    const parsed = new URL(urlValue);
    const normalizedPath = parsed.pathname.replace(/\/+$/, '') || '/';
    const normalized = `${parsed.origin}${normalizedPath}${parsed.search}`;
    return normalized.toLowerCase();
  } catch {
    return urlValue.trim().toLowerCase();
  }
}

/** The page a URL names, and how many pages the first search answered. */
export interface IPageByUrl {
  page?: ClientContentItemEntity;
  /** How many pages the search for the URL as given answered, whether or not one of them is the page. */
  candidateCount: number;
}

/**
 * The page a URL names. A page is known to a person by its URL, while everything that refers to a
 * page — an issue, a link into its own screen — names it by its key, which the service gives it and
 * which is not the URL. The content list answers a URL search with the pages stored under that URL
 * exactly, among their aliases too, so the URL is asked for as given; a search that is not read as a
 * URL answers pages whose text merely contains it, so the URL itself decides between the candidates.
 * When no page has the URL, it is asked for without its query, since a page is often crawled without
 * the query a visitor's address carries.
 */
export async function findPageByUrl(api: ISyncCoreApi, url: string): Promise<IPageByUrl> {
  const asGiven = url.trim();
  const wanted = normalizePageUrl(asGiven);
  const firstResult = await api.governance.contentItems.list({ page: 0, itemsPerPage: 25 }, { search: asGiven });
  const candidateCount = (firstResult.items || []).length;
  const exactMatch = (firstResult.items || []).find((candidate) => isKnownUnder(candidate, wanted, (address) => address));
  if (exactMatch) {
    return { page: exactMatch, candidateCount };
  }

  // A URL without a query has been asked for as it is, and nothing is left to ask.
  const withoutQuery = asGiven.replace(/\?.*$/, '');
  if (withoutQuery === asGiven) {
    return { page: undefined, candidateCount };
  }

  const fallbackResult = await api.governance.contentItems.list({ page: 0, itemsPerPage: 25 }, { search: withoutQuery });
  const fallbackMatch = (fallbackResult.items || []).find((candidate) =>
    isKnownUnder(candidate, normalizePageUrl(withoutQuery), (address) => address.replace(/\?.*$/, ''))
  );

  return { page: fallbackMatch, candidateCount };
}

/** Whether a page is stored under the wanted URL, as its own or as one of its aliases. */
function isKnownUnder(candidate: ClientContentItemEntity, wanted: string, reduce: (address: string) => string): boolean {
  return [candidate.url, ...(candidate.urlAliases || [])].some((address) => !!address && normalizePageUrl(reduce(address)) === wanted);
}
